import clsx from 'clsx'
import { useFormik } from 'formik'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Modal } from 'react-bootstrap'
import InputMask from 'react-input-mask'
import Select from '../../../components/textFields/AppSelect'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import {
    Autocomplete,
    Libraries,
    useJsApiLoader
} from '@react-google-maps/api'
import { KTSVG } from '../../../_metronic/helpers'
import TextField from '../../../components/textFields/TextField'
import { constraintConfig } from '../../../constraintConfig'
import { useGetAllDivisionQuery, useGetAllLocationsQuery } from '../../../services/GeneralApi'
import { useUpdateUserBasicInfoMutation } from '../../../services/authApi'
import { baseUrl } from '../../../services/baseUrl'
import labelKey from "../../localization/label.json"

type Props = {
    show: boolean
    handleClose: () => void
    data: any
}

const validationSchema = Yup.object().shape({
    email: Yup.string().when('isRequestCorporateCreditCard', {
        is: true,
        then: Yup.string().email('Wrong email format').required('Email is required'),
        otherwise: Yup.string().nullable(),
    }),
    employeeId: Yup.string().when('isRequestCorporateCreditCard', {
        is: true,
        then: Yup.string()
            .required('Employee ID is required')
            .min(1, 'Employee ID should not be empty')
            .test('is-unique', 'Employee ID should be unique', function (value) {
                if (value !== undefined && value !== null) {
                    return value !== '0' && !/^0+$/.test(value);
                }
                return false;
            }),
        otherwise: Yup.string().nullable(),
    }),
    firstName: Yup.string().when('isRequestCorporateCreditCard', {
        is: true,
        then: Yup.string().required('First Name is required'),
        otherwise: Yup.string().nullable(),
    }),
    lastName: Yup.string().when('isRequestCorporateCreditCard', {
        is: true,
        then: Yup.string().required('Last Name is required'),
        otherwise: Yup.string().nullable(),
    }),
    location: Yup.object().shape({
        id: Yup.number().when('isRequestCorporateCreditCard', {
            is: true,
            then: Yup.number().min(1, 'Office Location is required').required('Office Location is required'),
            otherwise: Yup.number().nullable(),
        }),
        value: Yup.string().nullable(),
    }),
    division: Yup.object().shape({
        id: Yup.number().when('isRequestCorporateCreditCard', {
            is: true,
            then: Yup.number().min(1, 'Division is required').required('Division is required'),
            otherwise: Yup.number().nullable(),
        }),
        title: Yup.string().nullable(),
    }),
    spendingLimit: Yup.number().when('isRequestCorporateCreditCard', {
        is: true,
        then: Yup.number()
            .typeError('Corp CC Limit is required')
            .min(1, 'Corp CC Limit must be greater than 0')
            .required('Corp CC Limit is required'),
        otherwise: Yup.number().nullable(),
    }),
    findAddress: Yup.string().when('isRequestCorporateCreditCard', {
        is: true,
        then: Yup.string().trim().required('Find Address is required'),
        otherwise: Yup.string().nullable(),
    }),
    addressLine1: Yup.string().when('isRequestCorporateCreditCard', {
        is: true,
        then: Yup.string().trim().required('Please select an address from Find Address suggestions'),
        otherwise: Yup.string().nullable(),
    }),
    isRequestCorporateCreditCard: Yup.boolean().required(),
    cellPhone: Yup.string().nullable(),
    businessPhone: Yup.string().nullable(),
    businessExt: Yup.string().nullable(),
    middleName: Yup.string().nullable(),
})

const initialValues = {
    userAccountID: 0,
    employeeId: '',
    firstName: '',
    middleName: '',
    lastName: '',
    cellPhone: '',
    businessPhone: '',
    businessExt: '',
    email: '',
    location: {
        id: 0,
        value: ''
    },
    division: {
        id: 0,
        title: ''
    },
    spendingLimit: 0,
    isRequestCorporateCreditCard: true,
    active: true,
    findAddress: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    latitude: '',
    longitude: '',
    country: {
        id: 0,
        title: ''
    },
}

const UpdateUserRequestCreditCard = ({ show, handleClose, data }: Props) => {
    const { data: locationsData, refetch: locationRefetch } = useGetAllLocationsQuery('')
    const { data: getAlldivision, refetch: divisionRefetch } = useGetAllDivisionQuery('')
    const [updateContact, { isSuccess: updateIsSuccess, isError, error, isLoading: updateIsLoading }] = useUpdateUserBasicInfoMutation()
    const [formValues, setFormValues] = useState(initialValues);

    const libraries: Libraries = useMemo(() => ['places'], []);
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: baseUrl.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries,
    });
    const originRef = useRef<HTMLInputElement>(null);
    const originAutocompleteRef = useRef<google.maps.places.Autocomplete>();

    useEffect(() => {
        if (show) {
            locationRefetch()
            divisionRefetch()
        }
    }, [show, locationRefetch, divisionRefetch])

    const handleModalClose = () => {
        setFormValues(formik.values);
        handleClose();
    };

    useEffect(() => {
        if (show && !data && Object.keys(formValues).length !== 0) {
            formik.setValues(formValues);
        }
        if (data) {
            formik.setValues({
                ...initialValues,
                ...data,
                location: data?.location || { id: 0, value: '' },
                division: data?.division || { id: 0, title: '' },
                country: data?.country || { id: 0, title: '' },
                spendingLimit: data?.spendingLimit || 0,
                isRequestCorporateCreditCard: true,
                findAddress: data?.findAddress || '',
                addressLine1: data?.addressLine1 || '',
                addressLine2: data?.addressLine2 || '',
                city: data?.city || '',
                state: data?.state || '',
                postalCode: data?.postalCode || '',
                latitude: data?.latitude || '',
                longitude: data?.longitude || '',
            })
        } else if (!show) {
            formik.resetForm();
        }
    }, [show, data]);

    const formik = useFormik({
        initialValues,
        validationSchema: validationSchema,
        onSubmit: (values) => {
            if (!values.findAddress?.trim() || !values.addressLine1?.trim()) {
                toast.error('Please correct address using Find Address and select a map result. This address will be used for your Corporate Credit Card for shipping.');
                return;
            }
            updateContact({
                ...values,
                location: {
                    id: values.location?.id || 0,
                    ID: values.location?.id || 0,
                    value: values.location?.value || '',
                },
                division: {
                    id: values.division?.id || 0,
                    Id: values.division?.id || 0,
                    title: values.division?.title || '',
                },
                isRequestCorporateCreditCard: true,
                requestccCardStatus: true,
                creditCardCardRequest: {
                    id: constraintConfig.ccCardRequestStatus.Requested,
                    title: 'Requested'
                }
            })
        },
    })

    useEffect(() => {
        if (updateIsSuccess) {
            toast.success('Corporate Credit Card request has been submitted successfully.');
            handleClose()
        }
    }, [updateIsSuccess]);

    useEffect(() => {
        if (isError && error) {
            let errorMessage: string = 'Something went wrong';
            if ('data' in error && error.data && typeof error.data === 'object' && 'message' in error.data) {
                errorMessage = (error.data as { message: string }).message;
            }
            toast.error(errorMessage);
        }
    }, [isError, error]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
        }
    };

    const handleLocationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.setFieldValue('findAddress', event.target.value);
        formik.setFieldValue('addressLine1', '');
        formik.setFieldValue('city', '');
        formik.setFieldValue('state', '');
        formik.setFieldValue('postalCode', '');
        formik.setFieldValue('latitude', '');
        formik.setFieldValue('longitude', '');
        formik.setFieldValue('country.id', 0);
        formik.setFieldValue('country.title', '');
    };

    const handleStartPlaceChanged = () => {
        const place = originAutocompleteRef.current?.getPlace();
        if (!place?.geometry?.location) return;

        const selectedLat = place.geometry.location.lat();
        const selectedLng = place.geometry.location.lng();
        const formattedAddress = place.formatted_address ?? '';

        formik.setFieldValue('latitude', selectedLat.toString());
        formik.setFieldValue('longitude', selectedLng.toString());
        formik.setFieldValue('findAddress', formattedAddress);

        if (place.address_components) {
            let city = '';
            let postalCode = '';
            let state = '';
            let streetNumber = '';
            let route = '';

            place.address_components.forEach((component) => {
                if (component.types.includes('street_number')) streetNumber = component.long_name;
                if (component.types.includes('route')) route = component.long_name;
                if (component.types.includes('locality')) {
                    city = component.long_name;
                    formik.setFieldValue('city', city);
                }
                if (component.types.includes('sublocality_level_1') && !city) {
                    city = component.long_name;
                    formik.setFieldValue('city', city);
                }
                if (component.types.includes('country')) {
                    formik.setFieldValue('country.id', 0);
                    formik.setFieldValue('country.title', component.long_name);
                }
                if (component.types.includes('postal_code')) {
                    postalCode = component.long_name;
                    formik.setFieldValue('postalCode', postalCode);
                }
                if (component.types.includes('administrative_area_level_1')) {
                    state = component.long_name;
                    formik.setFieldValue('state', state);
                }
            });

            const address = `${streetNumber} ${route}`.trim();
            formik.setFieldValue('addressLine1', address || formattedAddress);
        }
    };

    const spendingLimitValue = Number(formik.values.spendingLimit);
    const canSendRequest =
        Number.isFinite(spendingLimitValue)
        && spendingLimitValue > 0
        && Number(formik.values.division?.id) > 0
        && Number(formik.values.location?.id) > 0
        && Boolean(formik.values.firstName?.toString().trim())
        && Boolean(formik.values.lastName?.toString().trim())
        && Boolean(formik.values.employeeId?.toString().trim())
        && formik.values.employeeId?.toString().trim() !== '0'
        && !/^0+$/.test(formik.values.employeeId?.toString().trim() || '')
        && Boolean(formik.values.email?.toString().trim())
        && Yup.string().email().isValidSync(formik.values.email)
        && Boolean(formik.values.findAddress?.toString().trim())
        && Boolean(formik.values.addressLine1?.toString().trim());

    return (
        <>
            <Modal
                aria-hidden='true'
                dialogClassName='modal-dialog modal-xl modal-dialog-centered'
                show={show}
                onHide={handleModalClose}
                backdrop="static"
            >
                <div className='modal-header'>
                    <h2 className='mb-0'>Corporate Credit Card Request</h2>
                    <div className='btn btn-sm btn-icon btn-active-color-primary' onClick={handleClose}>
                        <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
                    </div>
                </div>

                <form className='form w-100' onSubmit={formik.handleSubmit}>
                    <div className='modal-body py-lg-4 px-lg-10'>
                        <div className='row fv-row'>
                                    <div className="col-md-4 mb-5">
                                        <TextField
                                            label="Corp CC Limit"
                                            required={true}
                                            type='number'
                                            placeholder='Enter Corp CC Limit'
                                            labelClass={`${formik.touched.spendingLimit && formik.errors.spendingLimit ? 'is-invalid' : ''}`}
                                            {...formik.getFieldProps('spendingLimit')}
                                        />
                                        {formik.touched.spendingLimit && formik.errors.spendingLimit && (
                                            <div className='fv-plugins-message-container'>
                                                <div className='fv-help-block'>
                                                    <span role='alert'>{formik.errors.spendingLimit}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="col-md-4 mb-5">
                                        <label className='form-label fs-5 fw-bold required'>{labelKey.division}</label>
                                        <Select
                                            isClearable={true}
                                            options={(getAlldivision?.result || []).map((option: any) => ({
                                                value: option.id,
                                                label: option?.value,
                                                key: option.id,
                                            }))}
                                            placeholder='Select Division'
                                            value={
                                                formik.values.division?.id
                                                    ? {
                                                        value: formik.values.division?.id,
                                                        label: formik.values.division?.title || '',
                                                    }
                                                    : null
                                            }
                                            onChange={(selectedOption: any) => {
                                                formik.setFieldValue(`division.id`, selectedOption?.value || null);
                                                formik.setFieldValue(`division.title`, selectedOption?.label || null);
                                            }}
                                        />
                                        {formik.touched.division?.id && formik.errors.division?.id && (
                                            <div className='fv-plugins-message-container'>
                                                <div className='fv-help-block'>
                                                    <span role='alert'>{formik.errors.division?.id}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className='col-md-4 mb-5'>
                                        <TextField
                                            label={labelKey.firstName}
                                            required={true}
                                            type='text'
                                            placeholder='Enter First Name'
                                            labelClass={`${formik.touched.firstName && formik.errors.firstName ? 'is-invalid' : ''}`}
                                            {...formik.getFieldProps('firstName')}
                                        />
                                        {formik.touched.firstName && formik.errors.firstName && (
                                            <div className='fv-plugins-message-container'>
                                                <div className='fv-help-block'>
                                                    <span role='alert'>{formik.errors.firstName}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className='col-md-4 mb-5'>
                                        <TextField
                                            label={labelKey.middleName}
                                            type='text'
                                            placeholder='Enter Middle Name'
                                            {...formik.getFieldProps('middleName')}
                                        />
                                    </div>

                                    <div className='col-md-4 mb-5'>
                                        <TextField
                                            label={labelKey.lastName}
                                            required={true}
                                            type='text'
                                            placeholder='Enter Last Name'
                                            labelClass={`${formik.touched.lastName && formik.errors.lastName ? 'is-invalid' : ''}`}
                                            {...formik.getFieldProps('lastName')}
                                        />
                                        {formik.touched.lastName && formik.errors.lastName && (
                                            <div className='fv-plugins-message-container'>
                                                <div className='fv-help-block'>
                                                    <span role='alert'>{formik.errors.lastName}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="col-md-4 mb-5">
                                        <TextField
                                            label={labelKey.employeeId}
                                            required={true}
                                            type='text'
                                            placeholder='Enter Employee ID'
                                            labelClass={`${formik.touched.employeeId && formik.errors.employeeId ? 'is-invalid' : ''}`}
                                            {...formik.getFieldProps('employeeId')}
                                        />
                                        {formik.touched.employeeId && formik.errors.employeeId && (
                                            <div className='fv-plugins-message-container'>
                                                <div className='fv-help-block'>
                                                    <span role='alert'>{formik.errors.employeeId}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="col-md-4 mb-5">
                                        <TextField
                                            label={labelKey.email}
                                            required={true}
                                            type='email'
                                            disabled={true}
                                            placeholder='Enter Email Address'
                                            labelClass={`${formik.touched.email && formik.errors.email ? 'is-invalid' : ''}`}
                                            {...formik.getFieldProps('email')}
                                        />
                                    </div>

                                    <div className='col-md-4 mb-5'>
                                        <label className='fs-5 fw-bold mb-2 required'>
                                            Office Location
                                        </label>
                                        <Select
                                            options={(locationsData?.result || []).map((option: any) => ({
                                                value: option.id,
                                                label: option?.value,
                                                key: option.id,
                                            }))}
                                            placeholder='Select Office Location'
                                            value={
                                                formik.values.location?.id
                                                    ? {
                                                        value: formik.values.location?.id,
                                                        label: formik.values.location?.value || '',
                                                    }
                                                    : null
                                            }
                                            onChange={(selectedOption: any) => {
                                                formik.setFieldValue(`location.id`, selectedOption?.value || null);
                                                formik.setFieldValue(`location.value`, selectedOption?.label || null);
                                            }}
                                        />
                                        {formik.touched.location?.id && formik.errors.location?.id && (
                                            <div className='fv-plugins-message-container'>
                                                <div className='fv-help-block'>
                                                    <span role='alert'>{formik.errors.location?.id}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className='col-md-4 mb-5'>
                                        <div className='fv-row'>
                                            <label className='form-label fs-5 fw-bold'>{labelKey.cellPhone}</label>
                                            <InputMask
                                                mask="(999) 999-9999"
                                                placeholder='Cell Phone'
                                                type='text'
                                                autoComplete='off'
                                                {...formik.getFieldProps('cellPhone')}
                                                value={formik.values.cellPhone || ''}
                                                className={clsx('form-control form-control-lg form-control-solid default-input')}
                                            />
                                        </div>
                                    </div>

                                    <div className='col-md-4 mb-5'>
                                        <div className='fv-row'>
                                            <label className='form-label fs-5 fw-bold'>{labelKey.businessPhone}</label>
                                            <InputMask
                                                mask="(999) 999-9999"
                                                placeholder='Business Phone'
                                                type='text'
                                                autoComplete='off'
                                                {...formik.getFieldProps('businessPhone')}
                                                value={formik.values.businessPhone || ''}
                                                className={clsx('form-control form-control-lg form-control-solid default-input')}
                                            />
                                        </div>
                                    </div>

                                    <div className='col-md-4 mb-5'>
                                        <div className='fv-row'>
                                            <label className='form-label fs-5 fw-bold'>{labelKey.businessExt}</label>
                                            <InputMask
                                                mask="9999"
                                                placeholder='Business Ext'
                                                type='text'
                                                autoComplete='off'
                                                {...formik.getFieldProps('businessExt')}
                                                value={formik.values.businessExt || ''}
                                                className='form-control form-control-lg form-control-solid default-input'
                                            />
                                        </div>
                                    </div>

                                    <div className='col-12 mb-3'>
                                        <Alert variant='warning' className='mb-3 py-3'>
                                            <p className='fs-7 mb-0'>
                                                This address will be used for your <b>Corporate Credit Card</b> for shipping. Please verify the Find Address selection carefully before continuing.
                                            </p>
                                        </Alert>
                                        <Alert variant='primary' className='mb-0 py-3'>
                                            <p className='fs-7 mb-0'>
                                                Please correct address using the Find Address field and select a result from the map suggestions.
                                                After you select an address, Address Line 1, City, State, Country, Postal Code, Latitude, and Longitude are filled automatically.
                                            </p>
                                        </Alert>
                                    </div>

                                    <div className='col-12 mb-4'>
                                        <label className='fs-5 fw-bold mb-2 required'>
                                          Find Home/Shipping Address
                                        </label>
                                        {isLoaded && (
                                            <Autocomplete
                                                onLoad={(autocomplete) => {
                                                    originAutocompleteRef.current = autocomplete;
                                                    autocomplete.addListener('place_changed', handleStartPlaceChanged);
                                                }}
                                                onPlaceChanged={() => { }}
                                            >
                                                <input
                                                    type='text'
                                                    className='form-control form-control-lg form-control-solid default-input'
                                                    placeholder='Type and select an address from the suggestions'
                                                    ref={originRef}
                                                    onKeyDown={handleKeyDown}
                                                    onChange={handleLocationChange}
                                                    value={formik.values.findAddress}
                                                />
                                            </Autocomplete>
                                        )}
                                        {formik.touched.findAddress && formik.errors.findAddress && (
                                            <div className='fv-plugins-message-container'>
                                                <div className='fv-help-block'>
                                                    <span role='alert'>{formik.errors.findAddress}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className='col-md-6 mb-4'>
                                        <TextField
                                            label={labelKey.addressLine1}
                                            type='text'
                                            placeholder='Address Line 1'
                                            {...formik.getFieldProps('addressLine1')}
                                            disabled={true}
                                        />
                                    </div>
                                    <div className='col-md-6 mb-4'>
                                        <TextField
                                            label={`${labelKey.addressLine2} (optional)`}
                                            type='text'
                                            placeholder='Apartment, suite, unit, etc.'
                                            {...formik.getFieldProps('addressLine2')}
                                        />
                                    </div>
                                    <div className='col-md-4 mb-4'>
                                        <TextField
                                            label={labelKey.city}
                                            type='text'
                                            placeholder='City'
                                            {...formik.getFieldProps('city')}
                                            disabled={true}
                                        />
                                    </div>
                                    <div className='col-md-4 mb-4'>
                                        <TextField
                                            label={labelKey.state}
                                            type='text'
                                            placeholder='State'
                                            {...formik.getFieldProps('state')}
                                            disabled={true}
                                        />
                                    </div>
                                    <div className='col-md-4 mb-4'>
                                        <TextField
                                            label={labelKey.country}
                                            type='text'
                                            placeholder='Country'
                                            value={formik.values.country?.title || ''}
                                            disabled={true}
                                        />
                                    </div>
                                    <div className='col-md-4 mb-4'>
                                        <TextField
                                            label={labelKey.postalCode}
                                            type='text'
                                            placeholder='Postal Code'
                                            {...formik.getFieldProps('postalCode')}
                                            disabled={true}
                                        />
                                    </div>
                                    <div className='col-md-4 mb-4'>
                                        <TextField
                                            label={labelKey.latitude}
                                            type='text'
                                            placeholder='Latitude'
                                            {...formik.getFieldProps('latitude')}
                                            disabled={true}
                                        />
                                    </div>
                                    <div className='col-md-4 mb-4'>
                                        <TextField
                                            label={labelKey.longitude}
                                            type='text'
                                            placeholder='Longitude'
                                            {...formik.getFieldProps('longitude')}
                                            disabled={true}
                                        />
                                    </div>
                        </div>
                    </div>

                    <div className='modal-footer justify-content-center'>
                        <button
                            type='button'
                            className='btn btn-lg btn-light'
                            onClick={handleClose}
                        >
                            {labelKey.cancel}
                        </button>
                        <button
                            type='submit'
                            className='btn btn-lg btn-primary'
                            disabled={updateIsLoading || !canSendRequest}
                        >
                            {!updateIsLoading && <span className='indicator-label'>Send Request</span>}
                            {updateIsLoading && (
                                <span className='indicator-progress' style={{ display: 'block' }}>
                                    {labelKey.pleaseWait}...{' '}
                                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                </span>
                            )}
                        </button>
                    </div>
                </form>
            </Modal>
        </>
    )
}

export default UpdateUserRequestCreditCard
